// Openings: your repertoires and courses, with what is due in each.

import { useEffect } from "react";
import { getJson } from "../app/api.ts";
import { setDrillBadge } from "../app/badge.ts";
import { fmtCount } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { navigate } from "../app/router.ts";
import { Pending, ScreenHead, SideMark, useLoad } from "../app/ui.tsx";

export interface RepertoireRow {
  id: number;
  name: string;
  color: string;
  source?: string;
  positions: number;
  games?: number;
  due?: number;
  new?: number;
  habits?: number;
  gaps?: number;
  flags?: number;
}

export function OpeningsScreen() {
  const [reps] = useLoad(() => getJson<{ repertoires?: RepertoireRow[]; due?: number }>("/api/repertoires"));
  const due = reps.state === "ready" ? reps.data.due || 0 : 0;
  useEffect(() => {
    if (reps.state === "ready") setDrillBadge(due);
  }, [reps.state, due]);
  const list = reps.state === "ready" ? reps.data.repertoires || [] : [];
  return (
    <div className="screen">
      <ScreenHead
        title="Openings"
        subtitle="Your lines, drilled spaced, and courses built from the opening book."
        many
        actions={
          <>
            <button type="button" className={"btn" + (due ? " btn-primary" : "")} onClick={() => navigate("/openings/drill")}>
              <Icon name="target" />
              {due ? `Drill ${due} due` : "Drill due"}
            </button>
            <button type="button" className="btn" onClick={() => navigate("/openings/learn")}>
              <Icon name="book" />
              Learn an opening
            </button>
            <button type="button" className="btn" title="Play moves for either side and see the gambits ahead" onClick={() => navigate("/openings/explore")}>
              <Icon name="search" />
              Find a gambit
            </button>
            <button type="button" className="btn" onClick={() => navigate("/openings/import")}>
              <Icon name="upload" />
              Import
            </button>
          </>
        }
      />
      {reps.state !== "ready" ? (
        <Pending value={reps} text="Loading your repertoires…" />
      ) : !list.length ? (
        <div className="empty card">
          <h2>No repertoires yet</h2>
          <p>Import a Lichess study or a PGN, or build a course for any named opening, and your games will start telling you where you leave it.</p>
          <div className="btn-row">
            <button type="button" className="btn btn-primary" onClick={() => navigate("/openings/learn")}>
              Learn an opening
            </button>
            <button type="button" className="btn" onClick={() => navigate("/openings/import")}>
              Import
            </button>
          </div>
        </div>
      ) : (
        <div className="game-list">
          {list.map((r) => (
            <button key={r.id} type="button" className="game-row rep-row" onClick={() => navigate(`/openings/${r.id}`)}>
              <SideMark color={r.color} />
              <div className="game-main">
                <div className="game-opp">{r.name}</div>
                <div className="game-sub">
                  {[
                    r.source === "book" ? "Course" : null,
                    `${fmtCount(r.positions)} positions`,
                    r.games ? `seen in ${fmtCount(r.games)} ${r.games === 1 ? "game" : "games"}` : "not in your games yet",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <div className="game-side">
                {r.due ? (
                  <span className="count due" title="Positions due for review">
                    {r.due} due
                  </span>
                ) : null}
                {r.new ? (
                  <span className="count neutral" title="Positions not drilled yet">
                    {r.new} new
                  </span>
                ) : null}
                {r.habits ? (
                  <span className="count opening" title="Moves your games keep playing instead of your line">
                    {r.habits} {r.habits === 1 ? "habit" : "habits"}
                  </span>
                ) : null}
                {r.gaps ? (
                  <span className="count clean" title="Replies your opponents play that the line does not cover">
                    {r.gaps} {r.gaps === 1 ? "gap" : "gaps"}
                  </span>
                ) : null}
                {r.flags ? (
                  <span className="count blunder" title="Moves the book's evaluation says lose">
                    {r.flags} flagged
                  </span>
                ) : null}
                <Icon name="chev" className="chev" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
